import type { ReactNode } from "react";

/**
 * EmptyState Component Props
 */
export interface EmptyStateProps {
  /** Icon to display (Lucide icon component or custom element) */
  readonly icon?: ReactNode;
  /** Main heading text */
  readonly title?: string;
  /** Descriptive message explaining the empty state */
  readonly message: string;
  /** Optional action button */
  readonly action?: {
    readonly label: string;
    readonly onClick: () => void;
  };
  /** Optional custom className for additional styling */
  readonly className?: string;
}

/**
 * EmptyState Component
 * Reusable component for displaying empty states across the application.
 * Supports custom icons, titles, messages, and optional action buttons.
 *
 * @example
 * ```tsx
 * <EmptyState
 *   icon={<Bookmark className="w-16 h-16" />}
 *   title="No Saved Items"
 *   message="Your watchlist is empty. Add titles you want to watch later."
 *   action={{
 *     label: "Explore Content",
 *     onClick: () => navigate('/browse')
 *   }}
 * />
 * ```
 */
export function EmptyState({ icon, title, message, action, className = "" }: EmptyStateProps) {
  return (
    <div className={`flex flex-col items-center justify-center py-16 px-4 ${className}`}>
      {icon && <div className="text-neutral-600 mb-4">{icon}</div>}
      {title && <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>}
      <p className="text-neutral-400 text-center max-w-md mb-6">{message}</p>
      {action && (
        <button
          onClick={action.onClick}
          className="px-6 py-3 bg-lumi-accent text-black font-semibold rounded-lg hover:bg-lumi-accent/90 transition-colors"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
